/**
 * INACTIVITY-DASH-001 — Inactivity Review Dashboard (exception management).
 * Daily Review Summary strip (needs_review / reviewed / updated_since_review /
 * no_exceptions), saved default threshold, employee exception table with
 * compact expandable timelines, filters/sorting, CSV export, Mark Day as
 * Reviewed. Calculation stays server-side (INACTIVITY-REPORT-001, read-only).
 * Route: /hr/breaks/inactivity — Break Management tab.
 * NOTE: Flagged Breaks (break-rule violations) are a separate concept and
 * remain untouched — this page covers unexplained TitleFlow inactivity only.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Download, ChevronRight, Pencil, Check, X,
  Users, UserX, Timer, Clock3, CheckCircle2, Search, PlusCircle, Eye, EyeOff, FileText, CalendarDays,
} from 'lucide-react';
import { BreakPageHeader, BreakKpiCard, BreakFilterBar, FilterField, BreakTableCard, BreakTh, BreakStatusPill, BreakAvatar, BreakEmptyState, BreakLoadingState, BreakErrorState, BreakPagination } from '../../components/hr/break/ui/BreakUi';
import { Button } from '../../components/ui/button';
import { formatDateTimeET } from '../../utils/formatters';
import { annotateSharedCoverage } from './inactivitySharedCoverage';
import { EmployeeSummary } from './InactivityEmployeeSummary';
import { InactivityExplanationDialog } from './InactivityExplanationDialog';
import { DayReviewConfirmDialog } from './DayReviewConfirmDialog';
import { fmtMinLong, EMP_STATUS, REVIEW_STATUS, pickSelectedDay, isValidRange, applyExplanationPatch } from './inactivitySummary';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
// eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional ET time-only
const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '—';
const fmtMin = (m) => m >= 60 ? `${Math.floor(m / 60)}h ${String(Math.round(m % 60)).padStart(2, '0')}m` : `${Math.round(m)}m`;
const fmtDT = (iso) => iso ? formatDateTimeET(iso) : '';
// eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional compact ET day label
const etDay = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' }) : '';
const fmtSpan = (fromIso, toIso) => {
  const from = fmtT(fromIso);
  const to = toIso ? fmtT(toIso) : '(open)';
  if (fromIso && toIso && etDay(fromIso) !== etDay(toIso)) {
    return `${from} (${etDay(fromIso)}) – ${to} (${etDay(toIso)})`;
  }
  return `${from} – ${to}`;
};
// eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional weekday label
const dayLabel = (ds) => new Date(ds + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const isoDaysAgo = (n) => { const d = new Date(); d.setUTCDate(d.getUTCDate() - n); return d.toISOString().slice(0, 10); };

const DAY_STATUS = {
  needs_review: { label: 'Needs Review', cls: 'bg-red-100 text-red-700 border-red-200' },
  reviewed: { label: 'Reviewed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  updated_since_review: { label: 'Updated Since Review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  no_exceptions: { label: 'No Exceptions', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const gapStatus = (g) => {
  if (g.resolved) {
    const who = g.coverage_detail?.length ? ` — ${g.coverage_detail.map(c => c.label).join(', ')}` : '';
    // INACTIVITY-BREAK-RECON-001 — surface a sub-threshold uncovered remainder (presentation only; calc unchanged)
    const rem = Math.round(g.unexplained_minutes || 0);
    return { label: `Explained${who}${rem > 0 ? ` · ${fmtMin(rem)} below threshold` : ''}`, cls: 'bg-emerald-100 text-emerald-700' };
  }
  if ((g.covered_break_minutes || 0) + (g.covered_downtime_minutes || 0) > 0)
    return { label: `Partial — ${g.coverage_detail.map(c => c.label).join(', ')} (${fmtMin(g.unexplained_minutes)} unexplained)`, cls: 'bg-amber-100 text-amber-800' };
  return { label: 'Needs Attention', cls: 'bg-red-100 text-red-700' };
};

const boundaryNote = (prev, g) => {
  if (!prev) return null;
  if (g.starts_at_session_start) return `Session restarted — new session at ${fmtT(g.inactive_from)}`;
  if (prev.inactive_to === g.inactive_from) return `1 activity recorded at ${fmtT(g.inactive_from)}`;
  return null;
};

const Timeline = ({ emp, threshold, date, canReview }) => {
  const navigate = useNavigate();
  const [showAllSessions, setShowAllSessions] = useState(false);
  const visibleSessions = showAllSessions ? emp.sessions : emp.sessions.slice(0, 6);
  return (
    <div className="px-0 pt-3" data-testid={`emp-timeline-${emp.user_id}`}>
      {emp.session_count > 1 && (
        <div className="mb-3 text-[11.5px] text-slate-500" data-testid="emp-sessions-detail">
          <span className="font-semibold text-slate-600">Attendance sessions ({emp.session_count}): </span>
          {visibleSessions.map((s, i) => (
            <span key={i}>{i > 0 && ' · '}{fmtT(s.login_at)} – {s.logout_at ? fmtT(s.logout_at) : '(open)'}</span>
          ))}
          {emp.sessions.length > 6 && (
            <button
              className="ml-2 text-blue-600 hover:underline font-medium"
              data-testid="sessions-toggle-btn"
              onClick={() => setShowAllSessions(v => !v)}
            >
              {showAllSessions ? 'show less' : `+${emp.sessions.length - 6} more`}
            </button>
          )}
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] font-semibold text-slate-700">Inactivity Timeline ({emp.gaps.length} gap{emp.gaps.length === 1 ? '' : 's'})</span>
        <span className="text-[11px] text-slate-400">Threshold: {threshold} minutes · times in ET</span>
      </div>
      <div className="space-y-1">
        {annotateSharedCoverage(emp.gaps).map((g, i) => {
          const st = gapStatus(g);
          const note = boundaryNote(emp.gaps[i - 1], g);
          return (
            <React.Fragment key={i}>
              {note && (
                <div className="pl-9 text-[10.5px] text-slate-400 italic" data-testid="boundary-note">— {note} —</div>
              )}
              <div className="flex items-center gap-3 bg-white border border-slate-100 rounded-md px-3 py-1.5" data-testid="timeline-row">
                <span className="h-5 w-5 shrink-0 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
                <span className="text-[12px] font-medium text-slate-800 w-40">{fmtT(g.inactive_from)} – {fmtT(g.inactive_to)}</span>
                <span className="text-[12px] text-slate-500 w-14">{fmtMin(g.gap_minutes)}</span>
                <span className={`text-[10.5px] font-semibold rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                {g.ongoing && <span className="text-[10.5px] font-semibold rounded-full px-2 py-0.5 bg-orange-100 text-orange-700">Ongoing</span>}
                {g.prompts?.some(p => !p.responded) && (
                  <span className="text-[10.5px] text-red-600 font-medium">prompt not answered</span>
                )}
              </div>
              {/* INACTIVITY-BREAK-RECON-001 Fix A — the full recorded break/downtime behind the explanation.
                  INACTIVITY-UI-GROUP-003 — one record referenced by several rows is shown in full once; later rows link to it. */}
              {g.coverage_detail?.filter(c => c.recorded_from).map((c, k) => c._shared_repeat ? (
                <div key={k} className="pl-9 text-[11px] text-slate-400 flex items-center gap-1.5" data-testid="recorded-coverage-repeat-row">
                  <span className="text-slate-300">↳</span>
                  Same {c.label} <span className="text-slate-500">({fmtT(c.recorded_from)} – {c.open_ended ? 'ongoing' : fmtT(c.recorded_to)})</span>
                  <span className="text-slate-300">·</span> {fmtMin(c.covered_minutes)} within this gap
                </div>
              ) : (
                <div key={k} className="pl-9 text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="recorded-coverage-row">
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${c.type === 'break' ? 'bg-emerald-400' : 'bg-sky-400'}`} />
                  Recorded {c.label}: <span className="font-medium text-slate-700">{fmtT(c.recorded_from)} – {c.open_ended ? 'ongoing' : fmtT(c.recorded_to)}</span>
                  <span className="text-slate-400">·</span> {fmtMin(c.recorded_minutes)} total
                  {Math.round(c.covered_minutes) !== Math.round(c.recorded_minutes) && <span className="text-slate-400">({fmtMin(c.covered_minutes)} within this gap)</span>}
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>
      {canReview && (
        <Button
          variant="outline" size="sm" className="mt-3 text-[12px]"
          data-testid="add-downtime-link"
          onClick={() => navigate(`/analytics/downtime?user_id=${emp.user_id}&date=${date}`)}
        >
          <PlusCircle className="h-3.5 w-3.5 mr-1.5" /> Add Downtime / Explanation
        </Button>
      )}
    </div>
  );
};

const STATUS_TONE = { unexplained: 'red', partially_explained: 'amber', explained: 'green' };
const COLS = 'grid-cols-[16px_32px_minmax(150px,1.6fr)_minmax(90px,1fr)_minmax(150px,1.4fr)_minmax(80px,.9fr)_minmax(80px,.8fr)_minmax(100px,1fr)_minmax(120px,1fr)_minmax(140px,1.1fr)_84px]';

// INACTIVITY-EXPLANATION-002 — per-employee review cell (separate from the inactivity exception status)
const ReviewCell = ({ emp }) => {
  if (!emp.flagged_gaps) return <span className="text-[11.5px] text-slate-400">—</span>;
  const rs = REVIEW_STATUS[emp.review_status] || REVIEW_STATUS.needs_review;
  const awaiting = emp.explained_periods > 0 && emp.review_status !== 'reviewed';
  const latest = (emp.explanations || []).reduce((a, x) => (!a || x.updated_at > a.updated_at ? x : a), null);
  return (
    <span className="min-w-0 flex flex-col gap-0.5" data-testid="emp-review-cell">
      <BreakStatusPill tone={rs.tone} testId="emp-review-chip">{rs.label}</BreakStatusPill>
      {awaiting && latest && (
        <span className="text-[10.5px] text-violet-700 font-medium truncate" title={`“${latest.explanation}” — ${latest.updated_by_name} (${latest.updated_by_role}) · ${formatDateTimeET(latest.updated_at)}`} data-testid="emp-awaiting-review">
          {emp.review_status === 'updated_since_review' ? 'Explanation edited' : 'New explanation'} · {latest.updated_by_name}
        </span>
      )}
    </span>
  );
};

const EmployeeRow = ({ emp, threshold, date, canReview, canExplain, onExplain, onReview, reviewing, delay = 0 }) => {
  const [open, setOpen] = useState(false);
  const st = EMP_STATUS[emp.employee_status] || EMP_STATUS.unexplained;
  const breaks = emp.breaks_taken || [];
  const toggle = () => setOpen(o => !o);
  return (
    <div className={`bm-rise bm-row border-b border-slate-100 last:border-b-0 ${open ? 'is-open' : ''}`} style={{ animationDelay: `${delay}ms` }} data-testid={`emp-row-${emp.user_id}`}>
      <div className={`grid ${COLS} items-center gap-3 px-4 py-2.5 min-w-[1180px]`}>
        <button type="button" className="text-slate-400 hover:text-slate-700 flex items-center justify-center" onClick={toggle} aria-expanded={open} aria-label={open ? 'Collapse employee' : 'Expand employee'} data-testid={`emp-expand-${emp.user_id}`}>
          <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        </button>
        <BreakAvatar name={emp.name} />
        <button type="button" onClick={toggle} className="text-left min-w-0">
          <span className="block text-[13.5px] font-semibold text-slate-900 truncate">{emp.name}</span>
          <span className="block text-[11px] text-slate-400 truncate">{emp.role || ''}</span>
        </button>
        <span className="text-[12.5px] text-slate-600 truncate">{emp.team || '—'}</span>
        <span className="min-w-0" data-testid="emp-clocked-span">
          <span className="block text-[12.5px] text-slate-700 truncate">{fmtSpan(emp.first_login, emp.last_logout)}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${emp.last_logout ? 'bg-rose-400' : 'bg-emerald-500'}`} />{emp.last_logout ? 'Clocked out' : 'Clocked in'}
          </span>
        </span>
        <span className="min-w-0" data-testid="emp-breaks-cell">
          <span className="block text-[13px] font-semibold text-slate-800">{breaks.length}</span>
          <span className="block text-[11px] text-slate-400 truncate">
            {breaks.length ? breaks.slice(0, 2).map(b => `${b.label} · ${fmtMinLong(b.minutes)}`).join(', ') + (breaks.length > 2 ? ` +${breaks.length - 2}` : '') : 'No breaks'}
          </span>
        </span>
        <span className="text-[13px] font-semibold text-slate-800 whitespace-nowrap">{emp.flagged_gaps} period{emp.flagged_gaps === 1 ? '' : 's'}</span>
        <span className={`text-[13.5px] font-bold tabular-nums ${emp.total_unexplained_minutes > 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="emp-unexplained">{fmtMin(emp.total_unexplained_minutes)}</span>
        <span className="flex items-center gap-1.5"><BreakStatusPill tone={STATUS_TONE[emp.employee_status] || 'red'} testId="emp-status-chip">{st.label}</BreakStatusPill>
          {emp.explained_periods > 0 && <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700" title={`${emp.explained_periods} of ${emp.flagged_gaps} periods explained`} data-testid="emp-explained-chip"><CheckCircle2 className="h-3 w-3" />{emp.explained_periods}/{emp.flagged_gaps}</span>}
        </span>
        <ReviewCell emp={emp} />
        <span className="flex items-center justify-end gap-1.5">
          <button type="button" onClick={toggle} title={open ? 'Hide details' : 'View details'} className={`bm-btn h-8 w-8 rounded-lg border flex items-center justify-center ${open ? 'border-indigo-200 bg-indigo-50 text-indigo-600' : 'border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-200'}`} data-testid={`emp-view-${emp.user_id}`}>
            {open ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
          {canExplain && (
            <button type="button" title="Add Explanation" disabled={!emp.flagged_gaps} onClick={() => onExplain?.(emp)} className="bm-btn h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-200 flex items-center justify-center disabled:opacity-40" data-testid={`emp-explain-${emp.user_id}`}>
              <FileText className="h-4 w-4" />
            </button>
          )}
        </span>
      </div>
      {open && (
        <div className="bm-expand" data-testid={`emp-expanded-${emp.user_id}`}>
          <EmployeeSummary emp={emp} date={date} canReview={canReview} canExplain={canExplain} onExplain={onExplain} onReview={onReview} reviewing={reviewing}>
            <Timeline emp={emp} threshold={threshold} date={date} canReview={false} />
          </EmployeeSummary>
        </div>
      )}
    </div>
  );
};

const EMP_PAGE = 25;
const SORT_KEYS = { name: 'name', team: 'team', gaps: 'gaps', unexplained: 'unexplained' };

export default function InactivityReportPage() {
  const [range, setRange] = useState({ start: isoDaysAgo(6), end: isoDaysAgo(0) });   // APPLIED range (drives loading)
  const [rangeDraft, setRangeDraft] = useState({ start: isoDaysAgo(6), end: isoDaysAgo(0) }); // edited range — no refresh until Apply
  const [days, setDays] = useState([]);
  const [canReview, setCanReview] = useState(false);
  const [defaultThreshold, setDefaultThreshold] = useState(15);
  const [editingThr, setEditingThr] = useState(false);
  const [thrDraft, setThrDraft] = useState('');
  const [loadingDays, setLoadingDays] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [marking, setMarking] = useState(false);
  const [filters, setFilters] = useState({ q: '', team: 'all', status: 'all', review: 'all', sort: 'unexplained' });
  const [empPage, setEmpPage] = useState(1);
  const [reportError, setReportError] = useState(null);
  const [daysError, setDaysError] = useState(null);
  const [explainEmp, setExplainEmp] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  useEffect(() => { setEmpPage(1); }, [filters, selectedDate]);

  // INACTIVITY-EXPLANATION-001 — patch one employee in place (no full reload; durations untouched)
  const patchEmployee = useCallback((userId, data) => {
    setReport((prev) => {
      if (!prev) return prev;
      return { ...prev, employees: prev.employees.map((e) => (e.user_id === userId ? applyExplanationPatch(e, data) : e)) };
    });
  }, []);


  const loadDays = useCallback(async (keepSelection) => {
    setLoadingDays(true);
    setDaysError(null);
    try {
      const { data } = await axios.get(`${API}/api/reports/inactivity-review/daily-summary`,
        { headers: authH(), params: { start: range.start, end: range.end } });
      setDays(data.days || []);
      setCanReview(!!data.can_review);
      setDefaultThreshold(data.default_threshold);
      const list = data.days || [];
      setSelectedDate(prev => pickSelectedDay(list, prev, keepSelection));
    } catch (e) {
      setDaysError(e?.response?.data?.detail || 'Failed to load daily summary');
      toast.error(e?.response?.data?.detail || 'Failed to load daily summary');
    } finally {
      setLoadingDays(false);
    }
  }, [range.start, range.end]);

  useEffect(() => { loadDays(true); }, [loadDays]);

  const rangeDirty = rangeDraft.start !== range.start || rangeDraft.end !== range.end;
  const applyRange = () => {
    if (!isValidRange(rangeDraft)) { toast.error('Choose a valid From → To range'); return; }
    setRange({ ...rangeDraft });  // only now does the day list / report refresh
  };

  const selectedDay = useMemo(() => days.find(d => d.date === selectedDate), [days, selectedDate]);

  const markEmployeeReviewed = useCallback(async (emp) => {
    setReviewingId(emp.user_id);
    try {
      const { data } = await axios.post(`${API}/api/reports/inactivity-review/${selectedDate}/employees/${emp.user_id}/mark-reviewed`,
        { threshold: selectedDay?.threshold_minutes }, { headers: authH() });
      patchEmployee(emp.user_id, { review: { reviewed_by_name: data.reviewed_by_name, reviewed_by_role: data.reviewed_by_role, reviewed_at: data.reviewed_at }, review_snapshot: data.review_snapshot });
      toast.success(`${emp.name} marked as reviewed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark as reviewed');
    } finally {
      setReviewingId(null);
    }
  }, [selectedDate, selectedDay?.threshold_minutes, patchEmployee]);

  useEffect(() => {
    if (!selectedDate || !selectedDay) { setReport(null); return; }
    let cancelled = false;
    (async () => {
      setLoadingReport(true);
      setReportError(null);
      try {
        const { data } = await axios.get(`${API}/api/reports/unexplained-inactivity`,
          { headers: authH(), params: { date: selectedDate, threshold: selectedDay.threshold_minutes } });
        if (!cancelled) setReport(data);
      } catch (e) {
        if (!cancelled) { setReport(null); setReportError(e?.response?.data?.detail || 'Failed to load day details'); toast.error(e?.response?.data?.detail || 'Failed to load day details'); }
      } finally {
        if (!cancelled) setLoadingReport(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate, selectedDay?.threshold_minutes, reloadTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveThreshold = async () => {
    const v = parseInt(thrDraft, 10);
    if (!v || v < 5 || v > 240) { toast.error('Threshold must be 5-240 minutes'); return; }
    try {
      await axios.put(`${API}/api/reports/inactivity-review/settings`, { threshold_minutes: v }, { headers: authH() });
      toast.success(`Default threshold saved: ${v} minutes`);
      setEditingThr(false);
      loadDays(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save threshold');
    }
  };

  // INACTIVITY-REVIEW-004 — whole-day pending check before completing the daily review
  const [dayConfirm, setDayConfirm] = useState(null);
  const postDayReview = async (acknowledgePending) => {
    setMarking(true);
    try {
      await axios.post(`${API}/api/reports/inactivity-review/${selectedDate}/mark-reviewed`,
        { threshold: selectedDay?.threshold_minutes, acknowledge_pending: acknowledgePending }, { headers: authH() });
      toast.success(`${dayLabel(selectedDate)} marked as reviewed${acknowledgePending ? ' (pending employee reviews acknowledged)' : ''}`);
      setDayConfirm(null);
      loadDays(true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.counts) { setDayConfirm(d.counts); return; }
      toast.error((typeof d === 'string' && d) || d?.message || 'Failed to mark day as reviewed');
    } finally {
      setMarking(false);
    }
  };

  const markReviewed = async () => {
    if (!selectedDate) return;
    setMarking(true);
    try {
      const { data } = await axios.get(`${API}/api/reports/inactivity-review/${selectedDate}/pending-employee-reviews`,
        { params: { threshold: selectedDay?.threshold_minutes }, headers: authH() });
      if (data.pending > 0) { setDayConfirm(data); setMarking(false); return; }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to check pending employee reviews');
      setMarking(false);
      return;
    }
    await postDayReview(false);
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/api/reports/unexplained-inactivity`, {
        headers: authH(), responseType: 'blob',
        params: { date: selectedDate, threshold: selectedDay?.threshold_minutes, format: 'csv' },
      });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: `unexplained_inactivity_${selectedDate}.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('CSV export failed'); }
  };

  const teams = useMemo(() => [...new Set((report?.employees || []).map(e => e.team).filter(Boolean))].sort(), [report]);
  const filtered = useMemo(() => {
    let rows = report?.employees || [];
    if (filters.q) { const q = filters.q.toLowerCase(); rows = rows.filter(e => (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q)); }
    if (filters.team !== 'all') rows = rows.filter(e => e.team === filters.team);
    if (filters.status !== 'all') rows = rows.filter(e => e.employee_status === filters.status);
    if (filters.review === 'pending') rows = rows.filter(e => e.flagged_gaps > 0 && (e.review_status || 'needs_review') !== 'reviewed');
    else if (filters.review !== 'all') rows = rows.filter(e => e.flagged_gaps > 0 && (e.review_status || 'needs_review') === filters.review);
    const sorters = {
      unexplained: (a, b) => b.total_unexplained_minutes - a.total_unexplained_minutes,
      gaps: (a, b) => b.flagged_gaps - a.flagged_gaps,
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      team: (a, b) => (a.team || '').localeCompare(b.team || ''),
    };
    return [...rows].sort(sorters[filters.sort] || sorters.unexplained);
  }, [report, filters]);

  // KPI series/trend: real per-day values from daily-summary (applied range, up to the selected day); comparison = previous day in range.
  const kpiHistory = useMemo(() => {
    const asc = [...days].reverse(); // API returns newest first
    const idx = asc.findIndex(d => d.date === selectedDate);
    if (idx < 0) return { series: {}, prev: null };
    const upto = asc.slice(0, idx + 1);
    const pick = (k) => upto.map(d => Number(d[k] || 0));
    return { series: { employees_in_scope: pick('employees_in_scope'), employees_flagged: pick('employees_flagged'), total_gaps: pick('total_gaps'), total_unexplained_minutes: pick('total_unexplained_minutes') }, prev: idx > 0 ? asc[idx - 1] : null };
  }, [days, selectedDate]);
  const trendFor = (k, cur, upIsBad) => {
    const prev = kpiHistory.prev ? Number(kpiHistory.prev[k] || 0) : null;
    const pct = prev == null ? null : prev === 0 ? (cur === 0 ? 0 : null) : Math.round(((cur - prev) / prev) * 100);
    return { pct, label: kpiHistory.prev ? 'vs previous day' : 'no prior day in range', upIsBad };
  };
  const pageRows = filtered.slice((empPage - 1) * EMP_PAGE, empPage * EMP_PAGE);
  const empPages = Math.max(1, Math.ceil(filtered.length / EMP_PAGE));
  const onSort = (k) => setFilters(f => ({ ...f, sort: SORT_KEYS[k] }));

  const selCls = 'h-9 text-[12.5px] border border-slate-200 rounded-md bg-white px-2.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-200';

  return (
    <>
      <div className="space-y-4" data-testid="inactivity-review-dashboard">
        <BreakPageHeader title="Inactivity Report" subtitle="Review inactivity exceptions, monitor live sessions, and take action on flagged breaks across the team."
          action={canReview && selectedDate && (
            <div className="flex flex-col items-end">
              <Button className="bm-btn h-9 px-3.5 text-[12.5px] bg-blue-600 hover:bg-blue-700 shadow-sm rounded-lg" onClick={markReviewed} disabled={marking} data-testid="mark-day-reviewed-btn">
                {marking ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                {selectedDay?.review ? 'Re-mark Day as Reviewed' : 'Mark Day as Reviewed'}
              </Button>
              <div className="text-[10.5px] text-slate-400 mt-1 whitespace-nowrap" data-testid="last-reviewed-info">
                {selectedDay?.review ? `Last reviewed: ${selectedDay.review.reviewed_by_name} · ${fmtDT(selectedDay.review.reviewed_at)}` : 'Last reviewed: Not reviewed'}
                {selectedDay?.review?.proceeded_despite_pending && <span className="ml-1 text-amber-600 font-medium" data-testid="last-reviewed-pending-note">· proceeded with {selectedDay.review.pending_employee_reviews} pending</span>}
              </div>
            </div>
          )} />

        {/* INACTIVITY-UI-FINAL-006 — KPI cards; sparklines/trends come from the real daily-summary rows (previous day in the applied range) */}
        {report && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="day-summary-chips">
            <BreakKpiCard testid="kpi-0" icon={Users} tone="green" label="Clocked-in Employees" value={report.summary.employees_in_scope} series={kpiHistory.series.employees_in_scope} trend={trendFor('employees_in_scope', report.summary.employees_in_scope, false)} delay={0} />
            <BreakKpiCard testid="kpi-1" icon={UserX} tone="red" label="Employees Flagged" value={report.summary.employees_flagged} emphasis={!!report.summary.employees_flagged} series={kpiHistory.series.employees_flagged} trend={trendFor('employees_flagged', report.summary.employees_flagged, true)} delay={60} />
            <BreakKpiCard testid="kpi-2" icon={Timer} tone="blue" label="Inactivity Periods" value={report.summary.total_gaps} series={kpiHistory.series.total_gaps} trend={trendFor('total_gaps', report.summary.total_gaps, true)} delay={120} />
            <BreakKpiCard testid="kpi-3" icon={Clock3} tone="purple" label="Total Unexplained Time" value={report.summary.total_unexplained_minutes} display={fmtMin(report.summary.total_unexplained_minutes)} emphasis={!!report.summary.total_unexplained_minutes} series={kpiHistory.series.total_unexplained_minutes} trend={trendFor('total_unexplained_minutes', report.summary.total_unexplained_minutes, true)} delay={180} />
          </div>
        )}

        {/* Premium filter bar — Date Range | Threshold | Apply · Day | Search | Team | Status | Sort | Export (Apply model unchanged) */}
        <BreakFilterBar testId="inactivity-control-row" actions={
          <Button variant="outline" onClick={exportCSV} disabled={!report} data-testid="export-csv-btn" className="bm-btn h-9 px-3 text-[12.5px] rounded-lg">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export
          </Button>
        }>
          <FilterField label="Date Range">
            <div className="flex items-center gap-1.5">
              <input data-testid="range-start-input" type="date" className={`${selCls} w-[128px]`} value={rangeDraft.start} max={rangeDraft.end} onChange={e => setRangeDraft(r => ({ ...r, start: e.target.value }))} />
              <span className="text-slate-400 text-[12px]">to</span>
              <input data-testid="range-end-input" type="date" className={`${selCls} w-[128px]`} value={rangeDraft.end} min={rangeDraft.start} max={isoDaysAgo(0)} onChange={e => setRangeDraft(r => ({ ...r, end: e.target.value }))} />
            </div>
          </FilterField>
          <FilterField label="Inactivity Threshold">
            {editingThr ? (
              <div className="flex items-center gap-1.5">
                <input data-testid="threshold-input" type="number" min="5" max="240" className={`${selCls} w-20`} value={thrDraft} onChange={e => setThrDraft(e.target.value)} />
                <button data-testid="threshold-save-btn" onClick={saveThreshold} className="text-emerald-600" title="Save threshold"><Check className="h-4 w-4" /></button>
                <button onClick={() => setEditingThr(false)} className="text-slate-400" title="Cancel"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className={`${selCls} flex items-center gap-2`}>
                <span className="font-semibold text-slate-800 whitespace-nowrap" data-testid="default-threshold-value">{defaultThreshold} minutes</span>
                {canReview && <button data-testid="threshold-edit-btn" onClick={() => { setThrDraft(String(defaultThreshold)); setEditingThr(true); }} className="text-slate-400 hover:text-blue-600"><Pencil className="h-3.5 w-3.5" /></button>}
              </div>
            )}
          </FilterField>
          <Button className="bm-btn h-9 px-3.5 text-[12.5px] bg-blue-600 hover:bg-blue-700 rounded-lg" onClick={applyRange} disabled={loadingDays || !rangeDirty} data-testid="apply-range-btn">
            {loadingDays ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1.5" />} Apply
          </Button>
          {days.length > 0 && (
            <FilterField label="Day">
              <div className="relative">
                <CalendarDays className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                <select data-testid="inactivity-day-select" value={selectedDate || ''} onChange={e => setSelectedDate(e.target.value)} className={`${selCls} pl-8 w-[200px]`} aria-label="Select day">
                  {days.map(d => <option key={d.date} value={d.date}>{dayLabel(d.date)} · {(DAY_STATUS[d.status] || DAY_STATUS.no_exceptions).label}</option>)}
                </select>
              </div>
            </FilterField>
          )}
          <FilterField label="Employee">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input data-testid="emp-search-input" className={`${selCls} pl-8 w-[160px]`} placeholder="Search employee…" value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} />
            </div>
          </FilterField>
          <FilterField label="Team">
            <select data-testid="team-filter" className={`${selCls} w-[112px]`} value={filters.team} onChange={e => setFilters(f => ({ ...f, team: e.target.value }))}>
              <option value="all">All Teams</option>
              {teams.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </FilterField>
          <FilterField label="Status">
            <select data-testid="emp-status-filter" className={`${selCls} w-[136px]`} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
              <option value="all">All Statuses</option>
              {Object.entries(EMP_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </FilterField>
          <FilterField label="Review">
            <select data-testid="emp-review-filter" className={`${selCls} w-[168px]`} value={filters.review} onChange={e => setFilters(f => ({ ...f, review: e.target.value }))}>
              <option value="all">All Review States</option>
              <option value="pending">Pending (Needs Review + Updated)</option>
              {Object.entries(REVIEW_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </FilterField>
          <FilterField label="Sort">
            <select data-testid="sort-select" className={`${selCls} w-[150px]`} value={filters.sort} onChange={e => setFilters(f => ({ ...f, sort: e.target.value }))}>
              <option value="unexplained">Most Unexplained</option>
              <option value="gaps">Most Inactivity</option>
              <option value="name">Employee (A–Z)</option>
              <option value="team">Team</option>
            </select>
          </FilterField>
        </BreakFilterBar>

        {loadingDays && <BreakLoadingState label="Loading days…" testId="inactivity-days-loading" />}
        {!loadingDays && daysError && <BreakErrorState title="Couldn't load the day list." detail={daysError} onRetry={() => loadDays(true)} testId="inactivity-days-error" />}
        {!loadingDays && !daysError && days.length === 0 && (
          <BreakEmptyState tone="neutral" icon={<CalendarDays className="h-5 w-5" />} title="No days in range" subtitle="Widen the date range and press Apply to load inactivity days." testId="inactivity-no-days" />
        )}

        {selectedDate && (
          <div className="space-y-2">
            {loadingReport ? (
              <BreakTableCard><BreakLoadingState label="Loading day details…" testId="inactivity-report-loading" /></BreakTableCard>
            ) : reportError ? (
              <BreakErrorState title="Couldn't load day details." detail={reportError} onRetry={() => setReloadTick(t => t + 1)} testId="inactivity-report-error" />
            ) : report && filtered.length === 0 ? (
              <BreakEmptyState tone={report.employees?.length ? 'neutral' : 'ok'} icon={report.employees?.length ? <Search className="h-5 w-5" /> : undefined}
                title={report.employees?.length ? 'No employees match the current filters.' : 'No inactivity exceptions for this day.'}
                subtitle={report.employees?.length ? 'Try clearing the search, team, status or review filter.' : `Everyone stayed within the ${selectedDay?.threshold_minutes}-minute threshold.`} testId="emp-list-empty" />
            ) : report && (
              <>
                <BreakTableCard testId="emp-table-card">
                  <div className="overflow-x-auto">
                    <div className={`grid ${COLS} items-center gap-3 px-4 min-w-[1180px] bm-thead-grid`} data-testid="emp-table-header">
                      <span /><span />
                      <BreakTh label="Employee" sortKey="name" sort={{ key: filters.sort, dir: 'asc' }} onSort={onSort} />
                      <BreakTh label="Team / Role" sortKey="team" sort={{ key: filters.sort, dir: 'asc' }} onSort={onSort} />
                      <BreakTh label="Clocked Time (ET)" />
                      <BreakTh label="Breaks" />
                      <BreakTh label="Inactivity" sortKey="gaps" sort={{ key: filters.sort, dir: 'desc' }} onSort={onSort} />
                      <BreakTh label="Unexplained Time" sortKey="unexplained" sort={{ key: filters.sort, dir: 'desc' }} onSort={onSort} />
                      <BreakTh label="Status" />
                      <BreakTh label="Review" />
                      <BreakTh label="Actions" align="right" className="text-right" />
                    </div>
                    <div data-testid="emp-list">
                      {pageRows.map((emp, i) => (
                        <EmployeeRow key={emp.user_id} emp={emp} threshold={selectedDay?.threshold_minutes} date={selectedDate} canReview={canReview}
                          canExplain={!!report.can_explain} onExplain={setExplainEmp} onReview={markEmployeeReviewed} reviewing={reviewingId === emp.user_id} delay={i * 30} />
                      ))}
                    </div>
                  </div>
                </BreakTableCard>
                <BreakPagination page={empPage} pages={empPages} shown={pageRows.length} total={filtered.length} noun="employees" meta={`${report.employees.length} in report · threshold ${selectedDay?.threshold_minutes} min · times in ET`}
                  onPrev={() => setEmpPage(p => Math.max(1, p - 1))} onNext={() => setEmpPage(p => Math.min(empPages, p + 1))} testId="emp-pagination" />
              </>
            )}
          </div>
        )}
      </div>
      <InactivityExplanationDialog open={!!explainEmp} onOpenChange={(o) => { if (!o) setExplainEmp(null); }}
        emp={explainEmp ? (report?.employees || []).find((e) => e.user_id === explainEmp.user_id) || explainEmp : null}
        date={selectedDate} canDowntime={canReview} onSaved={patchEmployee} />
      <DayReviewConfirmDialog open={!!dayConfirm} onOpenChange={(o) => { if (!o && !marking) setDayConfirm(null); }} date={selectedDate} counts={dayConfirm} busy={marking}
        onProceed={() => postDayReview(true)}
        onReviewPending={() => { setDayConfirm(null); setFilters(f => ({ ...f, review: 'pending', q: '', team: 'all', status: 'all' })); }} />
    </>
  );
}
